import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Clock, MapPin, PenLine, ShieldCheck } from "lucide-react";
import { IMAGES, WHY_FEATURES } from "@/data/site";
import { Reveal, SectionHead, Accent } from "@/components/Reveal";

const ICONS = { PenLine, ShieldCheck, Clock, MapPin };

const WhyChoose = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section ref={ref} className="relative py-24 sm:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <div className="relative order-2 lg:order-1">
          <div className="relative rounded-3xl overflow-hidden h-[420px] sm:h-[520px] border border-white/10">
            <motion.img
              src={IMAGES.road}
              alt="Road trip through the mountains"
              loading="lazy"
              style={{ y: imgY }}
              className="w-full h-[116%] object-cover -mt-[8%]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F14]/70 to-transparent" />
          </div>
          <div className="glass rounded-2xl px-6 py-5 absolute -bottom-6 left-6 right-6 sm:right-auto sm:min-w-[280px]">
            <p className="font-playfair italic text-lg text-[#F2C230]">"The road answers everything."</p>
            <p className="text-xs text-[#F5F5F0]/55 mt-1 tracking-wide">— every Orego driver, eventually</p>
          </div>
        </div>

        <div className="order-1 lg:order-2">
          <SectionHead
            eyebrow="Why Orego"
            title={<>Why travellers <Accent>choose</Accent> Orego Tours</>}
            text="We are not a booking engine. We are the team that picks up the phone at midnight and knows which dhaba makes the best parathas on NH-44."
          />
          <div className="mt-10 space-y-7">
            {WHY_FEATURES.map((f, i) => {
              const Icon = ICONS[f.icon];
              return (
                <Reveal key={f.title} delay={i * 0.09} y={24}>
                  <div className="flex gap-5 group">
                    <span className="w-12 h-12 shrink-0 rounded-2xl glass flex items-center justify-center text-[#3FB6D3] transition-transform duration-300 group-hover:-translate-y-1 group-hover:text-[#F2C230]">
                      <Icon size={20} />
                    </span>
                    <div>
                      <h3 className="font-sora text-lg font-semibold">{f.title}</h3>
                      <p className="text-sm text-[#F5F5F0]/60 leading-relaxed mt-1.5">{f.text}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChoose;
